import { resolvePath } from "../layout/resolution.js";
import {
  getOrCreateBehaviorFlowState,
  resolveBehaviorFlowStateKey,
} from "./flowState.js";
import { stableValueKey } from "./method.js";
import { resolveShortcutCombo } from "../blueprint/shortcut-keys.js";
import { runInitialFlows, runOnChangeFlows } from "./compileEvents.js";
import type {
  BehaviorFlowExecutionState,
  CompiledBehavior,
  CompiledEventHandler,
  FlowRunResult,
  RuntimeContext,
} from "./types.js";

export type { BehaviorFlowExecutionState };

function createEmptyFlowRunResult(): FlowRunResult {
  return {
    patches: [],
    effects: [],
    continuations: [],
  };
}

function appendFlowRunResult(target: FlowRunResult, source: FlowRunResult) {
  target.patches.push(...source.patches);
  target.effects.push(...source.effects);
  target.continuations.push(...source.continuations);
}

function buildOnChangeSignature(
  ctx: RuntimeContext,
  watchDependencies: string[],
): string {
  if (watchDependencies.length === 0) {
    return "";
  }

  return watchDependencies
    .map((dependency) =>
      stableValueKey(resolvePath(ctx, dependency), ctx.__signatureValueCache),
    )
    .join("|");
}

function resolveCustomEventTrigger(
  handler: CompiledEventHandler,
  ctx: RuntimeContext,
) {
  if (!handler.customEventId) {
    return undefined;
  }

  return ctx.__customEventTriggers?.[handler.customEventId];
}

function resolveCustomEventStateKey(
  ctx: RuntimeContext,
  ownerNodeId: string,
  triggerId: string,
): string {
  const instanceKey =
    ctx.__nodeInstanceKeyPrefix != null
      ? `${ctx.__nodeInstanceKeyPrefix}${ownerNodeId}`
      : ctx.__nodeInstanceKey
        ? `${ctx.__nodeInstanceKey}:${ownerNodeId}`
        : ownerNodeId;

  return `${instanceKey}:customEvent:${triggerId}`;
}

function shouldRunCustomEventHandler(
  handler: CompiledEventHandler,
  ctx: RuntimeContext,
  ownerNodeId: string,
  phase: "render" | "runtime",
): boolean {
  const trigger = resolveCustomEventTrigger(handler, ctx);
  if (!trigger) {
    return false;
  }

  const stateKey = resolveCustomEventStateKey(ctx, ownerNodeId, trigger.id);
  return trigger.shouldRun(ctx, stateKey, phase);
}

function collectWatchDependencies(behavior: CompiledBehavior): string[] {
  return behavior.eventHandlers
    .filter((handler) => handler.eventKind === "onChange")
    .flatMap((handler) => handler.watchDependencies ?? []);
}

function shouldRunRuntimeOnChangeHandler(
  handler: CompiledEventHandler,
  ctx: RuntimeContext,
  state: BehaviorFlowExecutionState,
): boolean {
  const watchDependencies = handler.watchDependencies ?? [];
  const signature = buildOnChangeSignature(ctx, watchDependencies);
  const signatures =
    state.lastRuntimeOnChangeSignatureByEventId ?? new Map<string, string>();
  state.lastRuntimeOnChangeSignatureByEventId = signatures;

  const previousSignature = signatures.get(handler.eventNodeId);
  signatures.set(handler.eventNodeId, signature);

  if (previousSignature == null) {
    const shouldRun = handler.triggerOnInit === true;
    return shouldRun;
  }

  const shouldRun = previousSignature !== signature;
  return shouldRun;
}

function shouldRunRenderOnChangeHandler(
  handler: CompiledEventHandler,
  ctx: RuntimeContext,
  state: BehaviorFlowExecutionState,
): boolean {
  const watchDependencies = handler.watchDependencies ?? [];
  const signature = buildOnChangeSignature(ctx, watchDependencies);
  const signatures =
    state.lastRenderOnChangeSignatureByEventId ?? new Map<string, string>();
  state.lastRenderOnChangeSignatureByEventId = signatures;

  const previousSignature = signatures.get(handler.eventNodeId);
  signatures.set(handler.eventNodeId, signature);

  if (previousSignature == null) {
    return handler.triggerOnInit === true;
  }

  return previousSignature !== signature;
}

function shouldRunControlRoomHandler(
  handler: CompiledEventHandler,
  ctx: RuntimeContext,
  state: BehaviorFlowExecutionState,
  phase: "render" | "runtime",
): boolean {
  const preference = handler.shortcutBinding
    ? ctx.__shortcutPreferences?.find(
        (item) => item.shortcut === handler.shortcutBinding,
      )
    : undefined;
  const combo = preference
    ? resolveShortcutCombo({ shortcutBinding: handler.shortcutBinding }, [
        preference,
      ])
    : handler.controlRoomCombo;
  if (!combo) {
    return false;
  }

  const matchingDispatches = (ctx.__controlRoomDispatches ?? []).filter(
    (dispatch) =>
      dispatch.combo === combo &&
      !ctx.__committedControlRoomDispatchIds?.has(dispatch.id),
  );
  if (matchingDispatches.length === 0) {
    return false;
  }

  if (ctx.__deferControlRoomDispatchConsumption) {
    return true;
  }

  const consumedByEvent =
    phase === "render"
      ? (state.consumedRenderControlRoomDispatchIdsByEventId ??
        new Map<string, Set<number>>())
      : (state.consumedRuntimeControlRoomDispatchIdsByEventId ??
        new Map<string, Set<number>>());
  if (phase === "render") {
    state.consumedRenderControlRoomDispatchIdsByEventId = consumedByEvent;
  } else {
    state.consumedRuntimeControlRoomDispatchIdsByEventId = consumedByEvent;
  }

  const consumedIds =
    consumedByEvent.get(handler.eventNodeId) ?? new Set<number>();
  consumedByEvent.set(handler.eventNodeId, consumedIds);

  let shouldRun = false;
  for (const dispatch of matchingDispatches) {
    if (consumedIds.has(dispatch.id)) {
      continue;
    }
    consumedIds.add(dispatch.id);
    shouldRun = true;
  }

  return shouldRun;
}

/**
 * Node-targeted gate for `onClick` handlers evaluated by `CompiledLayoutRenderer`
 * on any layout element. Mirrors {@link shouldRunControlRoomHandler}
 * but matches dispatches by `nodeId` instead of a shortcut combo.
 */
function shouldRunCustomDisciplineClickHandler(
  ctx: RuntimeContext,
  ownerNodeId: string,
): boolean {
  const matchingDispatches = (
    ctx.__customDisciplineClickDispatches ?? []
  ).filter(
    (dispatch) =>
      dispatch.nodeId === ownerNodeId &&
      (dispatch.nodeKey == null ||
        dispatch.nodeKey === resolveBehaviorFlowStateKey(ownerNodeId, ctx)) &&
      !ctx.__committedCustomDisciplineClickDispatchIds?.has(dispatch.id),
  );

  return matchingDispatches.length > 0;
}

function shouldRunListTransitionHandler(
  handler: CompiledEventHandler,
  ctx: RuntimeContext,
  state: BehaviorFlowExecutionState,
  phase: "render" | "runtime",
): boolean {
  if (
    handler.eventKind !== "onListEnter" &&
    handler.eventKind !== "onListExit" &&
    handler.eventKind !== "onListFilterEnter" &&
    handler.eventKind !== "onListFilterExit"
  ) {
    return false;
  }

  const matchingDispatches = (ctx.__listTransitionDispatches ?? []).filter(
    (dispatch) => dispatch.kind === handler.eventKind,
  );
  if (matchingDispatches.length === 0) {
    return false;
  }

  const consumedByEvent =
    phase === "render"
      ? (state.consumedRenderListTransitionDispatchIdsByEventId ??
        new Map<string, Set<number>>())
      : (state.consumedRuntimeListTransitionDispatchIdsByEventId ??
        new Map<string, Set<number>>());
  if (phase === "render") {
    state.consumedRenderListTransitionDispatchIdsByEventId = consumedByEvent;
  } else {
    state.consumedRuntimeListTransitionDispatchIdsByEventId = consumedByEvent;
  }

  const consumedIds =
    consumedByEvent.get(handler.eventNodeId) ?? new Set<number>();
  consumedByEvent.set(handler.eventNodeId, consumedIds);

  let shouldRun = false;
  for (const dispatch of matchingDispatches) {
    if (consumedIds.has(dispatch.id)) {
      continue;
    }
    consumedIds.add(dispatch.id);
    shouldRun = true;
  }

  return shouldRun;
}

function hasOnLoadRun(
  state: BehaviorFlowExecutionState,
  phase: "render" | "runtime",
): boolean {
  return (
    state.onLoadDoneByPhase?.[phase] === true ||
    (phase === "runtime" && state.onLoadDone === true)
  );
}

function markOnLoadRun(
  state: BehaviorFlowExecutionState,
  phase: "render" | "runtime",
): void {
  const onLoadDoneByPhase = state.onLoadDoneByPhase ?? {};
  onLoadDoneByPhase[phase] = true;
  state.onLoadDoneByPhase = onLoadDoneByPhase;

  if (phase === "runtime") {
    state.onLoadDone = true;
  }
}

/**
 * Re-runs declarative render flows on every `evaluate(data)`.
 * Use for onLoad/onChange/customEvent → setBinding, styles, etc.
 */
export function runRenderBehaviorFlows(
  behavior: CompiledBehavior,
  ctx: RuntimeContext,
  ownerNodeId: string,
): FlowRunResult {
  const result = createEmptyFlowRunResult();
  const isResumeEvaluation = (ctx.__resumeContinuations?.length ?? 0) > 0;
  const hasListTransitionDispatches =
    (ctx.__listTransitionDispatches?.length ?? 0) > 0;
  const flowStateKey = resolveBehaviorFlowStateKey(ownerNodeId, ctx);
  const state = getOrCreateBehaviorFlowState(ctx, flowStateKey);

  for (const handler of behavior.eventHandlers) {
    const resumeResult = handler.flow.run(ctx, { resumeOnly: true });
    appendFlowRunResult(result, resumeResult);
  }

  if (isResumeEvaluation && !hasListTransitionDispatches) {
    return result;
  }

  for (const handler of behavior.eventHandlers) {
    if (handler.eventKind === "customEvent") {
      if (!shouldRunCustomEventHandler(handler, ctx, ownerNodeId, "render")) {
        continue;
      }

      const flowResult = handler.flow.run(ctx);
      result.patches.push(...flowResult.patches);
      result.continuations.push(...flowResult.continuations);
      continue;
    }

    if (handler.eventKind === "onLoad") {
      const flowResult = handler.flow.run(ctx);
      result.patches.push(...flowResult.patches);
      if (!hasOnLoadRun(state, "render")) {
        result.continuations.push(...flowResult.continuations);
      }
    }

    if (handler.eventKind === "onClick") {
      const isLegacyIsolatedClickEval = ctx.__customDisciplineClick === true;
      if (
        !isLegacyIsolatedClickEval &&
        !shouldRunCustomDisciplineClickHandler(ctx, ownerNodeId)
      ) {
        continue;
      }
      const flowResult = handler.flow.run(ctx);
      result.patches.push(...flowResult.patches);
      result.effects.push(...flowResult.effects);
      result.continuations.push(...flowResult.continuations);
    }

    if (handler.eventKind === "onControlRoom") {
      if (!shouldRunControlRoomHandler(handler, ctx, state, "render")) {
        continue;
      }

      const flowResult = handler.flow.run(ctx);
      result.patches.push(...flowResult.patches);
      result.continuations.push(...flowResult.continuations);
    }

    if (
      handler.eventKind === "onListEnter" ||
      handler.eventKind === "onListExit" ||
      handler.eventKind === "onListFilterEnter" ||
      handler.eventKind === "onListFilterExit"
    ) {
      if (!shouldRunListTransitionHandler(handler, ctx, state, "render")) {
        continue;
      }

      const flowResult = handler.flow.run(ctx);
      result.patches.push(...flowResult.patches);
      result.continuations.push(...flowResult.continuations);
    }
  }

  if (!hasOnLoadRun(state, "render")) {
    markOnLoadRun(state, "render");
  }

  for (const handler of behavior.eventHandlers) {
    if (handler.eventKind !== "onChange") {
      continue;
    }

    if (!shouldRunRenderOnChangeHandler(handler, ctx, state)) {
      continue;
    }

    const flowResult = handler.flow.run(ctx);
    result.patches.push(...flowResult.patches);
    result.continuations.push(...flowResult.continuations);
  }

  return result;
}

/**
 * Runs runtime effects (animations, etc.) with onLoad / customEvent semantics.
 * Does not re-fire on mere data changes unless a custom-event trigger fires.
 */
export function runRuntimeBehaviorEffects(
  behavior: CompiledBehavior,
  ctx: RuntimeContext,
  ownerNodeId: string,
): FlowRunResult {
  const result = createEmptyFlowRunResult();
  const isResumeEvaluation = (ctx.__resumeContinuations?.length ?? 0) > 0;
  const hasListTransitionDispatches =
    (ctx.__listTransitionDispatches?.length ?? 0) > 0;
  if (isResumeEvaluation && !hasListTransitionDispatches) {
    return result;
  }
  const flowStateKey = resolveBehaviorFlowStateKey(ownerNodeId, ctx);
  const state = getOrCreateBehaviorFlowState(ctx, flowStateKey);

  if (!hasOnLoadRun(state, "runtime")) {
    for (const handler of behavior.eventHandlers) {
      if (handler.eventKind !== "onLoad") {
        continue;
      }

      const flowResult = handler.flow.run(ctx);
      result.effects.push(...flowResult.effects);
      if (!isResumeEvaluation) {
        result.continuations.push(...flowResult.continuations);
      }
    }

    markOnLoadRun(state, "runtime");
  }

  for (const handler of behavior.eventHandlers) {
    if (handler.eventKind !== "customEvent") {
      continue;
    }

    if (!shouldRunCustomEventHandler(handler, ctx, ownerNodeId, "runtime")) {
      continue;
    }

    const flowResult = handler.flow.run(ctx);
    result.effects.push(...flowResult.effects);
    if (!isResumeEvaluation) {
      result.continuations.push(...flowResult.continuations);
    }
  }

  for (const handler of behavior.eventHandlers) {
    if (handler.eventKind !== "onChange") {
      continue;
    }

    if (!shouldRunRuntimeOnChangeHandler(handler, ctx, state)) {
      continue;
    }

    const flowResult = handler.flow.run(ctx);
    result.effects.push(...flowResult.effects);
    if (!isResumeEvaluation) {
      result.continuations.push(...flowResult.continuations);
    }
  }

  for (const handler of behavior.eventHandlers) {
    if (handler.eventKind !== "onControlRoom") {
      continue;
    }

    if (!shouldRunControlRoomHandler(handler, ctx, state, "runtime")) {
      continue;
    }

    const flowResult = handler.flow.run(ctx);
    result.effects.push(...flowResult.effects);
    if (!isResumeEvaluation) {
      result.continuations.push(...flowResult.continuations);
    }
  }

  for (const handler of behavior.eventHandlers) {
    if (
      handler.eventKind !== "onListEnter" &&
      handler.eventKind !== "onListExit" &&
      handler.eventKind !== "onListFilterEnter" &&
      handler.eventKind !== "onListFilterExit"
    ) {
      continue;
    }

    if (!shouldRunListTransitionHandler(handler, ctx, state, "runtime")) {
      continue;
    }

    const flowResult = handler.flow.run(ctx);
    result.effects.push(...flowResult.effects);
    if (!isResumeEvaluation) {
      result.continuations.push(...flowResult.continuations);
    }
  }

  return result;
}

/**
 * Runs layout behavior flows with correct onLoad / onChange semantics.
 * State is kept in a per-compiled-node closure across `evaluate()` calls.
 */
export function runBehaviorFlows(
  behavior: CompiledBehavior,
  ctx: RuntimeContext,
  state: BehaviorFlowExecutionState,
): FlowRunResult {
  const result = createEmptyFlowRunResult();

  if (!state.onLoadDone) {
    appendFlowRunResult(result, runInitialFlows(behavior, ctx));
    state.onLoadDone = true;
  }

  const watchDependencies = collectWatchDependencies(behavior);
  const signature = buildOnChangeSignature(ctx, watchDependencies);

  if (signature !== state.lastOnChangeSignature) {
    appendFlowRunResult(result, runOnChangeFlows(behavior, ctx));
    state.lastOnChangeSignature = signature;
  }

  return result;
}

export function createBehaviorFlowExecutionState(): BehaviorFlowExecutionState {
  return { onLoadDone: false };
}
